import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import HeaderBackground from '../components/headerbackground.js';
import getAPI from '../functions/API/getAPI.js';
import GlobalContext from '../context/GlobalContext.js';
import moment from 'moment';
import { Calendar, Clock, Wrench, X } from 'lucide-react';

export default function Appointments() {
    const { settings, themeCSS } = useContext(GlobalContext);
    const { agreementId, agreementAddress, layoutLoading } = useOutletContext();
    const navigate = useNavigate();
    const primaryColor = settings?.primary_hex_colour || '#813468';

    const [loading, setLoading] = useState(true);
    const [appointments, setAppointments] = useState([]);
    const [filterTab, setFilterTab] = useState('upcoming');
    const [selected, setSelected] = useState(null);

    function getDaysUntil(dateStr) {
        const today = moment().startOf('day');
        const apptDate = moment(dateStr).startOf('day');
        const diff = apptDate.diff(today, 'days');
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Tomorrow';
        if (diff > 1) return `In ${diff} days`;
        return null;
    }

    function getIconColor(appointment) {
        if (appointment.is_complete) return { bg: 'bg-green-100', text: 'text-green-600' };
        const label = getDaysUntil(appointment.appointmentdate);
        if (label === 'Today') return { bg: 'bg-red-100', text: 'text-red-500' };
        if (label === 'Tomorrow') return { bg: 'bg-amber-100', text: 'text-amber-500' };
        return { bg: 'bg-blue-100', text: 'text-blue-500' };
    }

    function getBadge(appointment) {
        if (appointment.is_complete) {
            return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />Complete</span>;
        }
        const label = getDaysUntil(appointment.appointmentdate);
        if (!label) return null;
        if (label === 'Today') return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600"><span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />{label}</span>;
        if (label === 'Tomorrow') return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-600"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />{label}</span>;
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-600"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />{label}</span>;
    }

    async function fetchAppointments() {
        setLoading(true);
        const res = await getAPI('repairsappointments');
        if (res.response.ok && Array.isArray(res.data)) {
            setAppointments(res.data);
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchAppointments();
    }, []);

    const today = moment().format('YYYY-MM-DD');
    const filtered = appointments.filter(a => {
        const apptDate = moment(a.appointmentdate).format('YYYY-MM-DD');
        if (filterTab === 'upcoming') return apptDate >= today && !a.is_complete;
        return apptDate < today || a.is_complete;
    }).sort((a, b) => {
        if (filterTab === 'upcoming') return moment(a.appointmentdate).diff(moment(b.appointmentdate));
        return moment(b.appointmentdate).diff(moment(a.appointmentdate));
    });

    function SkeletonCard() {
        return (
            <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-4 flex items-start gap-3`}>
                <div className={`w-11 h-11 rounded-xl flex-shrink-0 ${themeCSS.skeleton} animate-pulse`} />
                <div className="flex-1 space-y-2 pt-0.5">
                    <div className={`h-4 w-2/3 rounded-full ${themeCSS.skeleton} animate-pulse`} />
                    <div className={`h-3 w-1/2 rounded-full ${themeCSS.skeleton} animate-pulse`} />
                    <div className={`h-3 w-4/5 rounded-full ${themeCSS.skeleton} animate-pulse`} />
                </div>
            </div>
        );
    }

    function AppointmentCard({ appointment }) {
        const colors = getIconColor(appointment);
        return (
            <button
                onClick={() => setSelected(appointment)}
                className={`w-full text-left ${themeCSS.cardBg} rounded-2xl shadow-sm p-4 flex items-start gap-3 ${themeCSS.cardHover} transition-colors`}
            >
                <div className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center ${colors.bg}`}>
                    <Calendar size={20} className={colors.text} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-semibold ${themeCSS.cardTitle} leading-snug`}>
                            {moment(appointment.appointmentdate).format('dddd Do MMMM')}
                        </p>
                        {getBadge(appointment)}
                    </div>
                    <p className={`text-xs mt-0.5 ${themeCSS.cardMuted}`}>
                        {appointment.starttime} – {appointment.endtime}
                        {appointment.repairid ? ` • Repair #${appointment.repairid}` : ''}
                    </p>
                    {appointment.repairdescription && (
                        <p className={`text-sm mt-1.5 ${themeCSS.cardText} line-clamp-2`}>
                            {appointment.repairdescription}
                        </p>
                    )}
                </div>
            </button>
        );
    }

    function DetailModal() {
        if (!selected) return null;
        const colors = getIconColor(selected);
        return (
            <div
                className="fixed inset-0 z-50 flex items-end lg:items-center justify-center"
                onClick={() => setSelected(null)}
            >
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                <div
                    className={`relative w-full lg:max-w-md ${themeCSS.cardBg} rounded-t-3xl lg:rounded-2xl shadow-xl p-6 z-10 max-h-[85vh] overflow-y-auto`}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Drag handle (mobile only) */}
                    <div className="lg:hidden flex justify-center mb-4">
                        <div className={`w-10 h-1 rounded-full ${themeCSS.skeleton}`} />
                    </div>

                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.bg}`}>
                                <Calendar size={22} className={colors.text} />
                            </div>
                            <div>
                                <p className={`text-base font-bold ${themeCSS.cardTitle}`}>
                                    {moment(selected.appointmentdate).format('dddd Do MMMM')}
                                </p>
                                <p className={`text-xs ${themeCSS.cardMuted}`}>
                                    {selected.starttime} – {selected.endtime}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setSelected(null)}
                            className={`p-2 rounded-full ${themeCSS.cardHover} ${themeCSS.cardMuted} transition-colors`}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {getBadge(selected) && (
                        <div className="mb-4">{getBadge(selected)}</div>
                    )}

                    {selected.repairdescription && (
                        <div className={`rounded-xl p-4 mb-3 ${themeCSS.pageBg}`}>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${themeCSS.cardMuted}`}>Description</p>
                            <p className={`text-sm ${themeCSS.cardText}`}>{selected.repairdescription}</p>
                        </div>
                    )}

                    <div className={`rounded-xl p-4 mb-3 ${themeCSS.pageBg} flex items-center gap-3`}>
                        <Clock size={16} className={themeCSS.cardMuted} />
                        <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Time</p>
                            <p className={`text-sm ${themeCSS.cardText}`}>{selected.starttime} – {selected.endtime}</p>
                        </div>
                    </div>

                    {selected.repairid && (
                        <div className={`rounded-xl p-4 ${themeCSS.pageBg} flex items-center gap-3`}>
                            <Wrench size={16} className={themeCSS.cardMuted} />
                            <div>
                                <p className={`text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Repair Reference</p>
                                <p className={`text-sm ${themeCSS.cardText}`}>#{selected.repairid}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${themeCSS.pageBg}`}>
            <HeaderBackground
                pageTitle="Appointments"
                onBack={() => navigate('/home')}
            />

            <div className="max-w-2xl mx-auto px-4 py-4 pb-32">
                {/* Filter tabs */}
                <div className={`mb-4 rounded-2xl shadow-sm p-1 flex ${themeCSS.cardBg}`}>
                    {['upcoming', 'past'].map(tab => {
                        const isActive = filterTab === tab;
                        return (
                            <button
                                key={tab}
                                onClick={() => setFilterTab(tab)}
                                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${isActive ? 'text-white' : themeCSS.cardMuted}`}
                                style={isActive ? { backgroundColor: primaryColor } : undefined}
                            >
                                {tab === 'upcoming' ? 'Upcoming' : 'Past'}
                            </button>
                        );
                    })}
                </div>

                {/* List */}
                <div className="space-y-3">
                    {loading ? (
                        <>
                            <SkeletonCard />
                            <SkeletonCard />
                            <SkeletonCard />
                        </>
                    ) : filtered.length === 0 ? (
                        <div className={`border-2 border-dashed ${themeCSS.cardBorder} rounded-2xl p-8 text-center`}>
                            <Calendar size={32} className={`mx-auto mb-3 ${themeCSS.cardMuted}`} />
                            <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>
                                No {filterTab} appointments
                            </p>
                            <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>
                                {filterTab === 'upcoming'
                                    ? 'You have no upcoming appointments scheduled.'
                                    : 'No past appointments to show.'}
                            </p>
                        </div>
                    ) : (
                        filtered.map((a, i) => <AppointmentCard key={i} appointment={a} />)
                    )}
                </div>
            </div>

            {DetailModal()}
        </div>
    );
}
